import React, { createContext, useContext, useEffect, useState } from 'react';
import platformConfigService from '../services/platformConfigService';
import inventoryService from '../services/inventoryService';
import testDataService from '../services/testDataService';
// Eliminado: import de firebaseRest (ya no existe)

// Declare window.auth to extend Window interface
declare global {
  interface Window {
    auth: any; // Replace 'any' with a more specific type if you have one for auth.js
  }
}

interface AuthContextType {
  user: any; // Firebase User object or null
  loading: boolean;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGoogleDirect: () => Promise<void>;
  logout: () => Promise<void>;
  checkUserSignInMethods: (email: string) => Promise<string[]>;
  linkGoogleAccount: () => Promise<void>;
  linkGoogleAccountWithVerification: () => Promise<void>;
  linkGoogleAccountSafe: () => Promise<void>;
  checkUserExists: (email: string) => Promise<boolean>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  verifyPasswordResetCode: (code: string) => Promise<string>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max
    let authStateUnsubscribe: (() => void) | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    
    // Wait for auth.js to be loaded
    const checkAuth = () => {
      console.log('Checking auth availability...', !!window.auth, 'Retry:', retryCount);
      if (window.auth && window.auth.onAuthStateChanged) {
        console.log('Auth is available, setting up listener');
        
        // Configurar el listener de estado de autenticación
        authStateUnsubscribe = window.auth.onAuthStateChanged(async (currentUser: any) => {
          console.log('Auth state changed:', currentUser);
          console.log('User providerData:', currentUser?.providerData);
          console.log('User email:', currentUser?.email);
          console.log('User uid:', currentUser?.uid);
          
          // Marcar que la autenticación está inicializada
          if (!authInitialized) {
            setAuthInitialized(true);
            console.log('✅ Firebase Auth inicializado correctamente');
          }
          
          // Limpiar timeout si existe
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          // Si hay un usuario autenticado, inicializar sus datos
          if (currentUser) {
            try {
              console.log('Inicializando datos del usuario...');
              
              // Inicializar datos del sistema de inventario
              await inventoryService.initializeDefaultData();
              console.log('✅ Datos de inventario inicializados');
              
              // Crear perfil de usuario si no existe
              const existingProfile = await inventoryService.getUserProfile();
              if (!existingProfile) {
                await inventoryService.createUserProfile({
                  email: currentUser.email || '',
                  displayName: currentUser.displayName || ''
                });
                console.log('✅ Perfil de usuario creado');
              }
              
              // Asegurar que las categorías existan
              try {
                const { ensureCategoriesExist } = await import('../utils/ensureCategoriesExist');
                await ensureCategoriesExist();
                console.log('✅ Categorías verificadas/creadas');
                
                // Ejecutar diagnóstico adicional
                const { fixCategoryDropdown } = await import('../utils/fixCategoryDropdown');
                await fixCategoryDropdown();
                console.log('✅ Diagnóstico de categorías completado');
              } catch (error) {
                console.warn('No se pudieron verificar/crear categorías:', error);
              }

              // Generar datos de prueba si no existen productos
              const products = await inventoryService.getProducts();
              if (products.length === 0) {
                console.log('🎯 Generando datos de prueba...');
                await testDataService.generateTestData();
                console.log('✅ Datos de prueba generados');
              }
              
              console.log('Usuario autenticado:', currentUser.email);
            } catch (error) {
              console.error('Error inicializando datos del usuario:', error);
              // No impedir el login por errores en la inicialización
              // pero logear el error para debugging
            }
          } else {
            console.log('No hay usuario autenticado');
          }
          
          setUser(currentUser);
          setLoading(false);
        });
        
        console.log('✅ Auth listener configurado correctamente');
      } else if (retryCount < maxRetries) {
        retryCount++;
        console.log('Auth not ready, retrying in 100ms');
        setTimeout(checkAuth, 100); // Retry after 100ms
      } else {
        console.error('Auth failed to load after maximum retries');
        console.log('Esperando respuesta de Firebase Auth...');
        
        // Dar más tiempo a Firebase para verificar la sesión persistente
        timeoutId = setTimeout(() => {
          if (loading && !authInitialized) {
            console.log('Firebase Auth no respondió en tiempo, verificando estado actual...');
            
            // Verificar sesión persistente en localStorage
            const hasPersistentSession = window.auth?.checkPersistentSession?.() || false;
            console.log('Sesión persistente encontrada:', hasPersistentSession);
            
            // Intentar verificar el estado actual del usuario
            const currentUser = window.auth?.getCurrentUser?.() || null;
            
            if (currentUser) {
              console.log('Usuario encontrado en auth.currentUser:', currentUser);
              setUser(currentUser);
            } else if (hasPersistentSession) {
              console.log('Hay sesión persistente pero no se puede obtener el usuario, esperando más...');
              // Si hay sesión persistente pero no se puede obtener el usuario, esperar un poco más
              setTimeout(() => {
                const retryUser = window.auth?.getCurrentUser?.() || null;
                if (retryUser) {
                  console.log('Usuario encontrado en segundo intento:', retryUser);
                  setUser(retryUser);
                } else {
                  console.log('No se pudo obtener usuario después de reintento');
                  setUser(null);
                }
                setAuthInitialized(true);
                setLoading(false);
              }, 2000);
              return; // No establecer loading false aquí
            } else {
              console.log('No hay usuario autenticado');
              setUser(null);
            }
            
            setAuthInitialized(true);
            setLoading(false);
          }
        }, 5000); // Esperar 5 segundos adicionales
      }
    };
    
    checkAuth();
    
    // Cleanup function para desuscribirse del listener
    return () => {
      if (authStateUnsubscribe) {
        console.log('Limpiando listener de autenticación');
        authStateUnsubscribe();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [authInitialized, loading]);

  const loginWithEmail = async (email: string, password: string) => {
    console.log('loginWithEmail called with:', email);
    if (!window.auth?.loginWithEmail) {
      console.error('Auth not ready or loginWithEmail not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.loginWithEmail...');
      await window.auth.loginWithEmail(email, password);
      console.log('Login successful');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    console.log('loginWithGoogle called - direct Google login/verification');
    if (!window.auth?.loginWithGoogle) {
      console.error('Auth not ready or loginWithGoogle not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.loginWithGoogle...');
      await window.auth.loginWithGoogle();
      console.log('Google login/verification successful');
    } catch (error) {
      console.error('Google login error:', error);
      // Re-throw the error with proper message handling
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const loginWithGoogleDirect = async () => {
    console.log('loginWithGoogleDirect called - direct Google login');
    if (!window.auth?.loginWithGoogle) {
      console.error('Auth not ready or loginWithGoogle not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.loginWithGoogle...');
      await window.auth.loginWithGoogle();
      console.log('Direct Google login successful');
    } catch (error) {
      console.error('Direct Google login error:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const logout = async () => {
    console.log('logout called');
    if (!window.auth?.logout) {
      console.error('Auth not ready or logout not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.logout...');
      await window.auth.logout();
      console.log('Logout successful');
      
      // Limpiar configuraciones de plataformas
      try {
        await platformConfigService.clearUserConfigs();
        console.log('✅ Configuraciones de plataformas limpiadas');
      } catch (configError) {
        console.warn('⚠️ Error limpiando configuraciones:', configError);
        // No lanzamos el error para no interrumpir el logout
      }
      
      // Force user state update
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  // Removed registerWithEmail as per user request
  // const registerWithEmail = async (email: string, password: string) => {
  //   setLoading(true);
  //   try {
  //     await window.auth.registerWithEmail(email, password);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const checkUserSignInMethods = async (email: string) => {
    console.log('checkUserSignInMethods called with:', email);
    if (!window.auth?.checkUserSignInMethods) {
      console.error('Auth not ready or checkUserSignInMethods not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    try {
      console.log('Calling window.auth.checkUserSignInMethods...');
      const methods = await window.auth.checkUserSignInMethods(email);
      console.log('Sign-in methods:', methods);
      return methods;
    } catch (error) {
      console.error('Error checking sign-in methods:', error);
      throw error;
    }
  };

  const linkGoogleAccount = async () => {
    console.log('linkGoogleAccount called');
    if (!window.auth?.linkGoogleAccount) {
      console.error('Auth not ready or linkGoogleAccount not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.linkGoogleAccount...');
      await window.auth.linkGoogleAccount();
      console.log('Google account linked successfully');
    } catch (error) {
      console.error('Error linking Google account:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const linkGoogleAccountWithVerification = async () => {
    console.log('linkGoogleAccountWithVerification called - using linkGoogleAccount');
    if (!window.auth?.linkGoogleAccount) {
      console.error('Auth not ready or linkGoogleAccount not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.linkGoogleAccount...');
      await window.auth.linkGoogleAccount();
      console.log('Google account linking with verification completed');
    } catch (error) {
      console.error('Error in Google account linking with verification:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const linkGoogleAccountSafe = async () => {
    console.log('linkGoogleAccountSafe called');
    if (!window.auth?.linkGoogleAccountSafe) {
      console.error('Auth not ready or linkGoogleAccountSafe not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.linkGoogleAccountSafe...');
      await window.auth.linkGoogleAccountSafe();
      console.log('Google account safe linking initiated');
    } catch (error) {
      console.error('Error in safe Google account linking:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const checkUserExists = async (email: string) => {
    console.log('checkUserExists called with:', email);
    if (!window.auth?.checkUserExists) {
      console.error('Auth not ready or checkUserExists not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    try {
      console.log('Calling window.auth.checkUserExists...');
      const exists = await window.auth.checkUserExists(email);
      console.log('User exists check result:', exists);
      return exists;
    } catch (error) {
      console.error('Error checking user existence:', error);
      throw error;
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    console.log('sendPasswordResetEmail called with:', email);
    if (!window.auth?.sendPasswordResetEmail) {
      console.error('Auth not ready or sendPasswordResetEmail not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.sendPasswordResetEmail...');
      await window.auth.sendPasswordResetEmail(email);
      console.log('Password reset email sent successfully');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const verifyPasswordResetCode = async (code: string) => {
    console.log('verifyPasswordResetCode called');
    if (!window.auth?.verifyPasswordResetCode) {
      console.error('Auth not ready or verifyPasswordResetCode not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    try {
      console.log('Calling window.auth.verifyPasswordResetCode...');
      const email = await window.auth.verifyPasswordResetCode(code);
      console.log('Password reset code verified successfully');
      return email;
    } catch (error) {
      console.error('Error verifying password reset code:', error);
      throw error;
    }
  };

  const confirmPasswordReset = async (code: string, newPassword: string) => {
    console.log('confirmPasswordReset called');
    if (!window.auth?.confirmPasswordReset) {
      console.error('Auth not ready or confirmPasswordReset not available');
      throw new Error('Sistema de autenticación no disponible');
    }
    setLoading(true);
    try {
      console.log('Calling window.auth.confirmPasswordReset...');
      await window.auth.confirmPasswordReset(code, newPassword);
      console.log('Password reset confirmed successfully');
    } catch (error) {
      console.error('Error confirming password reset:', error);
      throw error;
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithEmail, loginWithGoogle, loginWithGoogleDirect, logout, checkUserSignInMethods, linkGoogleAccount, linkGoogleAccountWithVerification, linkGoogleAccountSafe, checkUserExists, sendPasswordResetEmail, verifyPasswordResetCode, confirmPasswordReset }}>
      {children}
    </AuthContext.Provider>
  );
};


